import { MSSQL, sql } from "@codemirror/lang-sql";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { ChevronRight, Database, Loader2, Play, Table2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { NoticeBanner } from "@/components/ui/notice-banner";
import { PageNote } from "@/components/ui/page-legend";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/auth/use-auth";
import { outboundApi, type SfyConsoleQueryResult } from "@/features/outbound/outbound-api";
import { ensureElementVisible } from "@/lib/route-guards";
import { acceptCompletionOnTab } from "@/lib/sql-completions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_auth/konfiguracja/sql-sfy")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(context.queryClient, "sidebar.system.sql-sfy", location.href);
	},
	component: SqlSfyPage,
});

const DEFAULT_SQL = "SELECT TOP 100 * FROM dbo.HerbapolpTOWR";
const LIMIT_OPTIONS = [10, 50, 100, 250, 500, 1000];

function SqlSfyPage() {
	const { user } = useAuth();
	const isSuperAdmin = !!user?.roles?.includes("super_admin");
	const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);
	const [sqlText, setSqlText] = useState(DEFAULT_SQL);
	const [limit, setLimit] = useState(100);
	const [result, setResult] = useState<SfyConsoleQueryResult | null>(null);
	const [error, setError] = useState<string | null>(null);

	const connectionsQuery = useQuery({
		queryKey: ["outbound", "connections", "sql-sfy"],
		queryFn: () => outboundApi.listConnections(false),
		enabled: isSuperAdmin,
	});

	const selectedConnection = connectionsQuery.data?.find((c) => c.id === selectedConnectionId);

	const runMutation = useMutation({
		mutationFn: () => {
			if (!selectedConnectionId) throw new Error("Wybierz polaczenie");
			return outboundApi.runSfyConsoleQuery(selectedConnectionId, sqlText, limit);
		},
		onSuccess: (r) => {
			setResult(r);
			setError(null);
		},
		onError: (e: unknown) => {
			setResult(null);
			setError(e instanceof Error ? e.message : "Blad wykonania");
		},
	});

	if (!isSuperAdmin) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">Brak dostepu. Wymagana rola super_admin.</p>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-4 max-w-[1600px] max-md:p-3 max-md:space-y-3">
			<div>
				<h1 className="text-2xl font-semibold max-md:hidden">SQL klient (SFY)</h1>
				<PageNote id="about" title="O stronie">
					<p className="text-sm text-muted-foreground">
						Ad-hoc zapytania read-only do baz klientow SoftForYou (SQL Server). Tylko SELECT/WITH,
						limit 1000 wierszy, timeout 30s.
					</p>
				</PageNote>
			</div>

			<div className="flex items-center gap-3 flex-wrap">
				{connectionsQuery.isLoading ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Select
						value={selectedConnectionId ? String(selectedConnectionId) : ""}
						onValueChange={(v) => setSelectedConnectionId(Number(v))}
					>
						<SelectTrigger className="w-[460px] max-md:w-full max-md:[&>span]:min-w-0">
							<SelectValue placeholder="Wybierz polaczenie" />
						</SelectTrigger>
						<SelectContent>
							{(connectionsQuery.data ?? []).map((c) => (
								<SelectItem key={c.id} value={String(c.id)}>
									{c.clientName} — {c.name} ({c.host}:{c.port}/{c.databaseName})
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
				<Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
					<SelectTrigger className="w-[140px]">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{LIMIT_OPTIONS.map((n) => (
							<SelectItem key={n} value={String(n)}>
								limit {n}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Button
					onClick={() => runMutation.mutate()}
					disabled={!selectedConnectionId || runMutation.isPending || !sqlText.trim()}
					className="max-md:h-11 max-md:flex-1"
				>
					{runMutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<>
							<Play className="mr-2 h-4 w-4" /> Wykonaj
						</>
					)}
				</Button>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 min-h-[480px] max-md:min-h-0 max-md:gap-3">
				<TablesSidebar
					connectionId={selectedConnectionId}
					onPickTable={(qualified) =>
						setSqlText((s) =>
							s.trim().length === 0
								? `SELECT TOP ${limit} * FROM ${qualified}`
								: `${s.trimEnd()} ${qualified}`,
						)
					}
				/>

				{/* Telefon: edytor nad lista tabel (kolejnosc tylko w CSS, DOM bez zmian). */}
				<div className="space-y-3 min-w-0 max-md:order-first">
					{/* Wysokosc ROSNIE z trescia (min 180px, max 600px) zamiast byc sztywna —
					    przy jednej linii nie zieje pustka, a dlugie zapytania z lista VALUES
					    maja miejsce. Zawijanie wierszy, bo bez niego dlugi SELECT uciekal
					    poziomo poza ekran i trzeba bylo scrollowac w bok. */}
					<div className="border rounded-md overflow-hidden">
						<CodeMirror
							value={sqlText}
							onChange={(v) => setSqlText(v)}
							minHeight="180px"
							maxHeight="600px"
							extensions={[sql({ dialect: MSSQL }), EditorView.lineWrapping, acceptCompletionOnTab]}
							placeholder="-- SELECT * FROM dbo.HerbapolpTOWR WHERE BLOZ07 = '0123456'"
							className="text-sm"
						/>
					</div>

					{error && (
						<NoticeBanner
							tone="error"
							title="Blad"
							description={error}
							onDismiss={() => setError(null)}
						/>
					)}

					{result && <ResultPanel result={result} />}

					{!selectedConnection && (
						<p className="text-xs text-muted-foreground">
							Wybierz polaczenie z listy powyzej. Polaczenia konfigurujesz w /konfiguracja/sfy.
						</p>
					)}
				</div>
			</div>
		</div>
	);
}

function TablesSidebar({
	connectionId,
	onPickTable,
}: {
	connectionId: number | null;
	onPickTable: (qualified: string) => void;
}) {
	const schemaQuery = useQuery({
		queryKey: ["outbound", "schema", connectionId],
		queryFn: () => outboundApi.getConnectionSchema(connectionId as number),
		enabled: connectionId != null,
		staleTime: 5 * 60 * 1000,
	});
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const grouped = useMemo(() => {
		if (!schemaQuery.data) return new Map<string, { table: string; cols: string[] }[]>();
		const cols = schemaQuery.data.columns;
		const tables = schemaQuery.data.tables;
		const map = new Map<string, { table: string; cols: string[] }[]>();
		for (const t of tables) {
			const tcols = cols
				.filter((c) => c.tableSchema === t.tableSchema && c.tableName === t.tableName)
				.sort((a, b) => a.ordinalPosition - b.ordinalPosition)
				.map((c) => c.columnName);
			const arr = map.get(t.tableSchema) ?? [];
			arr.push({ table: t.tableName, cols: tcols });
			map.set(t.tableSchema, arr);
		}
		return map;
	}, [schemaQuery.data]);

	if (connectionId == null) {
		return (
			<div className="border rounded-md p-3 text-xs text-muted-foreground">Brak polaczenia.</div>
		);
	}
	if (schemaQuery.isLoading) {
		return (
			<div className="border rounded-md p-3 flex items-center gap-2 text-xs">
				<Loader2 className="h-3 w-3 animate-spin" /> Ladowanie schematu...
			</div>
		);
	}
	if (schemaQuery.isError) {
		return (
			<div className="border rounded-md p-3 text-xs text-destructive">
				Blad: {schemaQuery.error instanceof Error ? schemaQuery.error.message : "nieznany"}
			</div>
		);
	}

	function toggle(key: string) {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}

	return (
		<div className="border rounded-md text-xs overflow-y-auto max-h-[560px] max-md:max-h-80">
			<div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
				<Database className="h-3 w-3" />
				<span className="font-medium">Tabele klienta</span>
			</div>
			{Array.from(grouped.entries()).map(([schemaName, tables]) => (
				<div key={schemaName}>
					<div className="px-3 py-1 font-mono text-muted-foreground text-[10px] uppercase tracking-wider">
						{schemaName}
					</div>
					{tables.map(({ table, cols }) => {
						const key = `${schemaName}.${table}`;
						const isOpen = expanded.has(key);
						const qualified = `${schemaName}.${table}`;
						return (
							<div key={key}>
								<div className="flex items-center gap-1 px-2 hover:bg-accent">
									<button
										type="button"
										onClick={() => toggle(key)}
										className="p-1 max-md:flex max-md:size-9 max-md:items-center max-md:justify-center"
										title="Pokaz kolumny"
									>
										<ChevronRight
											className={cn("h-3 w-3 transition-transform", isOpen && "rotate-90")}
										/>
									</button>
									<Table2 className="h-3 w-3 text-muted-foreground" />
									<button
										type="button"
										onClick={() => onPickTable(qualified)}
										className="flex-1 text-left py-1 font-mono max-md:min-h-9 max-md:min-w-0 max-md:break-all"
										title="Wstaw do edytora"
									>
										{table}
									</button>
								</div>
								{isOpen && (
									<div className="ml-7 border-l border-border pl-2 py-1 space-y-0.5">
										{cols.map((c) => (
											<button
												type="button"
												key={c}
												onClick={() => onPickTable(`[${c}]`)}
												className="block w-full text-left py-0.5 font-mono text-[11px] text-muted-foreground hover:text-foreground max-md:py-2 max-md:break-all"
											>
												{c}
											</button>
										))}
									</div>
								)}
							</div>
						);
					})}
				</div>
			))}
		</div>
	);
}

function ResultPanel({ result }: { result: SfyConsoleQueryResult }) {
	return (
		<div className="space-y-2">
			<div className="flex items-center gap-3 text-xs text-muted-foreground max-md:flex-wrap max-md:gap-x-2 max-md:gap-y-1">
				<span>
					<strong className="text-foreground">{result.rowCount}</strong> wierszy
				</span>
				<span>·</span>
				<span>{result.durationMs} ms</span>
				{result.truncated && (
					<span className="text-amber-600">· obciety do limitu — dodaj WHERE/TOP zeby zawezic</span>
				)}
			</div>
			<div className="border rounded-md overflow-auto max-h-[480px]">
				<table className="w-full text-xs">
					<thead className="sticky top-0 z-10 bg-background shadow-[inset_0_-1px_0_var(--border)]">
						<tr>
							{result.columns.map((c) => (
								<th key={c} className="text-left p-2 font-mono font-medium bg-muted">
									{c}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{result.rows.length === 0 ? (
							<tr>
								<td
									colSpan={Math.max(1, result.columns.length)}
									className="p-4 text-center text-muted-foreground italic"
								>
									brak wynikow
								</td>
							</tr>
						) : (
							result.rows.map((row, ri) => (
								<tr
									// biome-ignore lint/suspicious/noArrayIndexKey: result rows are string[][] without stable id; index is natural.
									key={`r-${ri}`}
									className="border-t hover:bg-muted/20"
								>
									{row.map((v, ci) => (
										<td
											// biome-ignore lint/suspicious/noArrayIndexKey: columns are positional, no stable id.
											key={`r-${ri}-c-${ci}`}
											className="p-2 font-mono align-top break-all max-w-[400px]"
										>
											{v == null ? <em className="text-muted-foreground">NULL</em> : v}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}
